/**
 * @fileoverview Vuex mutations for WhatsApp state management.
 *
 * @module store/modules/whatsapp/mutations
 *
 * Handles synchronous state updates for:
 * - Message templates
 * - Application and profile settings
 * - Server status and configurations
 * - Groups and flows
 * - Download progress tracking
 */

import { MutationTree } from "vuex/types";
import Vue from "vue";
import { WhatsAppState } from "./types";

const mutations: MutationTree<WhatsAppState> = {
  /**
   * Sets the WhatsApp message templates.
   *
   * @param payload - Array of message templates
   */
  SET_WHATSAPP_MESSAGE_TEMPLATES(state, payload) {
    state.templates = payload;
  },
  SET_WHATSAPP_APPLICATION_SETTINGS(state, payload) {
    state.application_settings = payload;
  },
  SET_WHATSAPP_PROFILE_SETTINGS(state, payload) {
    state.profile_settings = payload;
  },
  SET_WHATSAPP_SERVER_STATUS(state, payload) {
    state.server_status = payload;
  },
  SET_WHATSAPP_GROUPS(state, payload) {
    state.groups = payload;
  },
  SET_WHATSAPP_SERVERS(state, payload) {
    Vue.set(state, "servers", payload);
  },
  SET_WA_FLOWS(state, payload) {
    Vue.set(state, "flows", payload);
  },
  SET_SELECTED_FLOW(state, payload) {
    if (!payload) {
      Vue.set(state, "selectedFlow", {
        preview: {},
        json: {},
      });
    }
    Vue.set(state, "selectedFlow", payload);
  },
  /**
   * Initializes download state with total count.
   *
   * @param payload.total - Total number of records to download
   */
  SET_WA_DOWNLOAD_START(state, payload: { total: number }) {
    Vue.set(state, "download", {
      ...state.download,
      isDownloading: true,
      total: payload.total || 0,
      processed: 0,
      progress: 0,
    });
  },
  /**
   * Stores the generated password for the encrypted export file.
   *
   * @param payload.password - Generated password for Excel file
   */
  SET_WA_DOWNLOAD_PASSWORD(state, payload: { password: string }) {
    Vue.set(state, "download", {
      ...state.download,
      generatedPassword: payload.password || "",
    });
  },
  /**
   * Updates download progress.
   *
   * @param payload.processed - Number of records processed
   * @param payload.progress - Progress percentage (0-100)
   */
  SET_WA_DOWNLOAD_PROGRESS(state, payload: { processed: number; progress: number }) {
    Vue.set(state, "download", {
      ...state.download,
      processed: payload.processed,
      progress: payload.progress,
    });
  },
  /**
   * Marks download as complete.
   */
  SET_WA_DOWNLOAD_FINISH(state) {
    Vue.set(state, "download", {
      ...state.download,
      isDownloading: false,
    });
  },
  /**
   * Resets download state to initial values.
   */
  RESET_WA_DOWNLOAD(state) {
    Vue.set(state, "download", {
      isDownloading: false,
      progress: 0,
      processed: 0,
      total: 0,
      generatedPassword: "",
    });
  },
};

export default mutations;
